import React from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
  "#06b6d4", "#f97316", "#14b8a6", "#a855f7", "#e11d48", "#84cc16",
];

/**
 * Distribution/density chart for continuous scale questions.
 * Shows overlaid area curves per condition — reveals clustering,
 * bimodality, or skew that bar charts of means would hide.
 */
const DistributionChart = ({ valueDistribution, conditions, scaleInfo, height = 220 }) => {
  if (!valueDistribution?.byCondition || Object.keys(valueDistribution.byCondition).length === 0) {
    return <p className="text-gray-500 text-sm">No distribution data</p>;
  }

  const conditionIds = conditions.map((c) => c.conditionId);
  const conditionLabels = {};
  conditions.forEach((c) => {
    conditionLabels[c.conditionId] = c.label || c.conditionId;
  });

  // Build range of possible values — detect from actual data keys
  // (likert composites have 0.5 steps, button_scales have integer steps)
  const allKeys = new Set();
  Object.values(valueDistribution.byCondition).forEach((dist) => {
    Object.keys(dist).forEach((k) => allKeys.add(Number(k)));
  });
  const values = [...allKeys].sort((a, b) => a - b);

  // If no data keys found, fall back to scale range
  if (values.length === 0) {
    const minVal = scaleInfo?.min ?? 1;
    const maxVal = scaleInfo?.max ?? scaleInfo?.points ?? 7;
    const step = scaleInfo?.step ?? 1;
    for (let v = minVal; v <= maxVal; v += step) values.push(v);
  }

  // Build chart data: each point = { value, [conditionId]: proportion }
  // Normalize to proportions (not raw counts) since cell sizes may differ
  const chartData = values.map((v) => {
    const point = { value: v };
    for (const cid of conditionIds) {
      const dist = valueDistribution.byCondition[cid] || {};
      const count = dist[String(v)] || 0;
      const total = Object.values(dist).reduce((s, c) => s + c, 0);
      point[cid] = total > 0 ? count / total : 0;
    }
    return point;
  });

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-xs shadow-lg">
        <p className="text-gray-300 font-medium mb-1">Value: {label}</p>
        {payload.map((entry, i) => (
          <p key={i} style={{ color: entry.color }}>
            {conditionLabels[entry.dataKey] || entry.dataKey}: {(entry.value * 100).toFixed(0)}%
          </p>
        ))}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
        <XAxis
          dataKey="value"
          stroke="#9ca3af"
          tick={{ fill: "#9ca3af", fontSize: 11 }}
        />
        <YAxis
          stroke="#9ca3af"
          tick={{ fill: "#9ca3af", fontSize: 11 }}
          tickFormatter={(v) => `${(v * 100).toFixed(0)}%`}
        />
        <Tooltip content={<CustomTooltip />} />
        <Legend
          formatter={(value) => (
            <span className="text-gray-300 text-xs">
              {conditionLabels[value] || value}
            </span>
          )}
        />
        {conditionIds.map((cid, i) => (
          <Area
            key={cid}
            type="monotone"
            dataKey={cid}
            stroke={CONDITION_COLORS[i % CONDITION_COLORS.length]}
            fill={CONDITION_COLORS[i % CONDITION_COLORS.length]}
            fillOpacity={0.15}
            strokeWidth={2}
          />
        ))}
      </AreaChart>
    </ResponsiveContainer>
  );
};

export default DistributionChart;
